<template>
  <div
    ref="track"
    class="slide-track relative h-[60px] w-full touch-none select-none overflow-hidden rounded-full bg-brown-50"
    :class="{ 'opacity-60': disabled }"
    :aria-disabled="disabled"
    role="button"
    tabindex="0"
    :aria-label="label"
    @keydown.enter.prevent="confirmViaKeyboard"
    @keydown.space.prevent="confirmViaKeyboard"
  >
    <!-- Filling trail behind the handle -->
    <div
      class="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-brown-100/60"
      :style="{ width: handleX + 56 + 'px', transition: trailTransition }"
    />

    <!-- Label centered across the full track so the amount stays fully visible
         even on narrow widths (≤320px). On wide tracks the resting handle sits
         clear of the centered label; on narrow tracks the handle overlaps only
         the leading word (standard slide-to-pay pattern) while the amount at the
         end stays readable. -->
    <div class="pointer-events-none absolute inset-0 flex items-center justify-center px-3">
      <span
        class="text-body-md truncate font-semibold text-brown-600"
        :style="{ opacity: labelOpacity }"
      >
        {{ label }}
      </span>
    </div>

    <!-- Draggable handle -->
    <div
      class="slide-handle absolute top-1 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brown-800 shadow-md"
      :class="disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'"
      :style="{ left: '4px', transform: `translateX(${handleX}px)`, transition: handleTransition }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </div>
  </div>
</template>

<script>
import { triggerImpact } from '@/utils/haptics';

const HANDLE_WIDTH = 52;
const TRACK_PADDING = 4;
// Fraction of the track the handle must cross to count as a confirm.
const CONFIRM_THRESHOLD = 0.88;

export default {
  name: 'SlideToGive',

  props: {
    label: {
      type: String,
      default: 'Slide to pay',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['confirm'],

  data() {
    return {
      handleX: 0,
      maxX: 0,
      dragging: false,
      confirmed: false,
      crossedThreshold: false,
    };
  },

  computed: {
    labelOpacity() {
      if (!this.maxX) return 1;
      return Math.max(0, 1 - (this.handleX / this.maxX) * 1.4);
    },

    handleTransition() {
      return this.dragging ? 'none' : 'transform 220ms cubic-bezier(0.32,0.72,0,1)';
    },

    trailTransition() {
      return this.dragging ? 'none' : 'width 220ms cubic-bezier(0.32,0.72,0,1)';
    },
  },

  beforeUnmount() {
    this.dragging = false;
  },

  methods: {
    measure() {
      const track = this.$refs.track;
      if (!track) return 0;
      this.maxX = track.getBoundingClientRect().width - HANDLE_WIDTH - TRACK_PADDING * 2;
      return this.maxX;
    },

    onPointerDown(event) {
      if (this.disabled || this.confirmed) return;
      this.measure();
      try {
        event.target.setPointerCapture(event.pointerId);
      } catch {
        // setPointerCapture unsupported — drag still works via move events
      }
      this.dragging = true;
      this.crossedThreshold = false;
    },

    onPointerMove(event) {
      if (!this.dragging) return;
      const track = this.$refs.track;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const x = event.clientX - rect.left - TRACK_PADDING - HANDLE_WIDTH / 2;
      this.handleX = Math.max(0, Math.min(this.maxX, x));

      // Light haptic tick the moment the user crosses the confirm threshold.
      const past = this.handleX >= this.maxX * CONFIRM_THRESHOLD;
      if (past && !this.crossedThreshold) {
        this.crossedThreshold = true;
        triggerImpact('light');
      } else if (!past) {
        this.crossedThreshold = false;
      }
    },

    onPointerUp() {
      if (!this.dragging) return;
      this.dragging = false;

      if (this.maxX > 0 && this.handleX >= this.maxX * CONFIRM_THRESHOLD) {
        this.complete();
      } else {
        this.handleX = 0;
      }
    },

    confirmViaKeyboard() {
      if (this.disabled || this.confirmed) return;
      this.measure();
      this.complete();
    },

    complete() {
      this.confirmed = true;
      this.handleX = this.maxX;
      triggerImpact('medium');
      this.$emit('confirm');
    },

    /**
     * Reset the handle to the start. Call from the parent (via ref) when a
     * payment attempt fails and the user should be able to slide again.
     */
    reset() {
      this.confirmed = false;
      this.crossedThreshold = false;
      this.handleX = 0;
    },
  },
};
</script>
